import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageTitle from '../../FormElements/PageTitle';
import { DraftSpectrumVisualPanel } from '../../DraftBuildSpectrum/DraftBuildSpectrumVisuals';
import {
	buildDraftPlan,
	CONSTRUCTION_MODES,
	formatRosterCounts,
	normalizeConstructionMode,
	POSITION_COLORS,
	POSITIONS,
	ROUND_COUNT_OPTIONS
} from '../../../utils/draftBuildSpectrum';

const SCORING_OPTIONS = [
	{ value: 'half_ppr', label: 'Half Pt PPR' },
	{ value: 'full_ppr', label: 'Full Pt PPR' },
	{ value: 'tep', label: 'TEP' },
	{ value: 'standard', label: 'Standard' }
];

const FORMAT_OPTIONS = [
	{ value: 'managed', label: 'Managed' },
	{ value: 'best_ball', label: 'Best Ball' }
];

function PositionBar({ weights, compact = false }) {
	return (
		<div className={`flex w-full overflow-hidden rounded-md ${compact ? 'h-2' : 'h-3'}`}>
			{POSITIONS.map((pos) => (
				<div
					key={pos}
					className={`${POSITION_COLORS[pos].bg} transition-all duration-300`}
					style={{ width: `${weights[pos] * 100}%` }}
					title={`${pos} ${Math.round(weights[pos] * 100)}%`}
				/>
			))}
		</div>
	);
}

function PositionBadge({ position, size = 'sm' }) {
	const colors = POSITION_COLORS[position];
	const sizeClass = size === 'lg' ? 'text-sm px-3 py-1' : 'text-xs px-2 py-0.5';
	return (
		<span
			className={`inline-flex items-center font-semibold rounded-md border ${colors.border} ${colors.soft} ${colors.text} ${sizeClass}`}
		>
			{position}
		</span>
	);
}

function RosterCountPills({ counts }) {
	return (
		<div className="flex flex-wrap gap-2">
			{POSITIONS.map((pos) => (
				<div
					key={pos}
					className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1 ${POSITION_COLORS[pos].border} ${POSITION_COLORS[pos].soft}`}
				>
					<span className={`text-xs font-bold ${POSITION_COLORS[pos].text}`}>{pos}</span>
					<span className="text-sm font-semibold text-gray-900 dark:text-dark-text">{counts[pos]}</span>
				</div>
			))}
		</div>
	);
}

function ConstructionModeToggle({ mode, onChange }) {
	return (
		<div className="flex gap-2">
			{CONSTRUCTION_MODES.map((option) => (
				<button
					key={option}
					type="button"
					onClick={() => onChange(option)}
					className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wide border transition-colors ${
						mode === option
							? 'bg-blue-600 text-white border-blue-600'
							: 'bg-gray-100 border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-dark-border dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 dark:hover:border-gray-500'
					}`}
				>
					{option.charAt(0).toUpperCase() + option.slice(1)}
				</button>
			))}
		</div>
	);
}

function SpectrumSlider({ value, onChange, constructionMode }) {
	const isHero = constructionMode === 'hero';
	const leftLabel = isHero ? 'Hero RB' : 'Zero RB';
	const rightLabel = isHero ? 'Hero WR' : 'Zero WR';

	return (
		<div>
			<div className="flex justify-between mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
				<span className="text-green-600 dark:text-green-400">{leftLabel}</span>
				<span className="text-gray-700 dark:text-gray-300">Balanced</span>
				<span className="text-blue-600 dark:text-blue-400">{rightLabel}</span>
			</div>

			<div className="relative" style={{ height: '32px' }}>
				<div
					className="absolute left-0 right-0 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden pointer-events-none"
					style={{ top: '14px', height: '4px' }}
					aria-hidden
				>
					<div className="h-full w-full bg-gradient-to-r from-green-500 via-gray-400 to-blue-500" />
				</div>

				<div
					className="absolute bg-gray-400 dark:bg-gray-500 pointer-events-none"
					style={{
						left: '50%',
						marginLeft: '-1px',
						top: '10px',
						width: '2px',
						height: '12px'
					}}
					aria-hidden
				/>

				<div
					className="absolute w-4 h-4 rounded-full bg-white dark:bg-gray-100 border-2 border-gray-800 dark:border-white shadow pointer-events-none z-10"
					style={{
						top: '8px',
						left: `${value}%`,
						marginLeft: '-8px'
					}}
					aria-hidden
				/>

				<input
					type="range"
					min={0}
					max={100}
					step={1}
					value={value}
					onChange={(e) => onChange(Number(e.target.value))}
					className="absolute left-0 w-full cursor-pointer z-20"
					style={{ top: '4px', height: '24px', opacity: 0, margin: 0 }}
					aria-label="Draft build spectrum"
					aria-valuemin={0}
					aria-valuemax={100}
					aria-valuenow={value}
				/>
			</div>

			<p className="text-center mt-3 text-gray-800 dark:text-dark-text">
				<span className="text-lg font-bold">{value}</span>
				<span className="text-sm text-gray-500 dark:text-gray-400 ml-1">/ 100</span>
			</p>
		</div>
	);
}

function EliteToggle({ label, description, enabled, onChange, accentClass, idleClass }) {
	const idle =
		idleClass ||
		'border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500';
	return (
		<label
			className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
				enabled ? `${accentClass} border-current` : idle
			}`}
		>
			<input
				type="checkbox"
				checked={enabled}
				onChange={(e) => onChange(e.target.checked)}
				className="mt-0.5 h-4 w-4 rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500 focus:ring-offset-0 dark:border-gray-500 dark:bg-dark-card dark:text-blue-400 dark:focus:ring-offset-dark-card"
			/>
			<span>
				<span className="text-sm font-semibold text-gray-900 dark:text-dark-text block">{label}</span>
				<span className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 block">{description}</span>
			</span>
		</label>
	);
}

function RoundCard({ round, isSelected, onSelect }) {
	const colors = POSITION_COLORS[round.primary];

	return (
		<button
			type="button"
			onClick={() => onSelect(round.round)}
			className={`text-left w-full rounded-lg border p-3 transition-all duration-200 ${
				isSelected
					? `ring-2 ring-offset-1 ring-blue-500 dark:ring-offset-dark-card ${colors.soft} ${colors.border}`
					: round.isElitePick
						? `border-2 ${colors.border} ${colors.soft}`
						: round.revisitPositionLabel
							? 'border-dashed border-gray-400 dark:border-gray-500 bg-white dark:bg-dark-card'
							: 'border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card hover:border-gray-300 dark:hover:border-gray-500'
			}`}
		>
			<div className="flex items-center justify-between gap-2 mb-2">
				<span className="text-xs font-bold text-gray-500 dark:text-gray-400">R{round.round}</span>
				<div className="flex items-center gap-1">
					{(round.isHeroRbAnchor || round.isHeroWrAnchor) && (
						<span
							className={`text-[10px] font-bold uppercase tracking-wide ${
								round.isHeroWrAnchor
									? 'text-yellow-700 dark:text-yellow-300'
									: 'text-green-700 dark:text-green-300'
							}`}
						>
							{round.isHeroWrAnchor ? 'Hero WR' : 'Hero RB'}
						</span>
					)}
					{round.revisitPositionLabel && (
						<span className="text-[10px] font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300">
							{round.revisitPositionLabel}
						</span>
					)}
					{(round.isHeroRbOpen || round.isHeroWrOpen) && !round.revisitPositionLabel && (
						<span
							className={`text-[10px] font-bold uppercase tracking-wide ${
								round.isHeroWrOpen
									? 'text-yellow-700 dark:text-yellow-300'
									: 'text-green-700 dark:text-green-300'
							}`}
						>
							Hero
						</span>
					)}
					{round.isLateQB && (
						<span className="text-[10px] font-bold uppercase tracking-wide text-yellow-700 dark:text-yellow-300">
							Late QB
						</span>
					)}
					{round.isLateTE && (
						<span className="text-[10px] font-bold uppercase tracking-wide text-purple-700 dark:text-purple-300">
							Late TE
						</span>
					)}
					{round.isElitePick && (
						<span className="text-[10px] font-bold uppercase tracking-wide text-yellow-700 dark:text-yellow-300">
							Elite
						</span>
					)}
					<PositionBadge position={round.primary} />
				</div>
			</div>
			<PositionBar weights={round.weights} compact />
			<p className="mt-2 text-xs text-gray-600 dark:text-gray-300 line-clamp-2">{round.tier}</p>
		</button>
	);
}

export default function DraftBuildSpectrumPage() {
	const [spectrumValue, setSpectrumValue] = useState(50);
	const [constructionMode, setConstructionMode] = useState('zero');
	const [totalRounds, setTotalRounds] = useState(18);
	const [selectedRound, setSelectedRound] = useState(1);
	const [spectrumView, setSpectrumView] = useState('map');
	const [eliteQB, setEliteQB] = useState(false);
	const [eliteTE, setEliteTE] = useState(false);
	const [lateQB, setLateQB] = useState(false);
	const [lateTE, setLateTE] = useState(false);
	const [draftScoring, setDraftScoring] = useState('half_ppr');
	const [draftFormat, setDraftFormat] = useState('best_ball');

	const plan = useMemo(
		() =>
			buildDraftPlan(spectrumValue, totalRounds, {
				constructionMode: normalizeConstructionMode(constructionMode),
				eliteQB,
				eliteTE,
				lateQB,
				lateTE,
				format: draftFormat,
				scoring: draftScoring
			}),
		[
			spectrumValue,
			constructionMode,
			totalRounds,
			eliteQB,
			eliteTE,
			lateQB,
			lateTE,
			draftFormat,
			draftScoring
		]
	);

	const activeRound =
		plan.rounds.find((r) => r.round === selectedRound) || plan.rounds[0];

	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20 text-gray-800 dark:text-dark-text">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="[&_h1]:mb-0 [&_h1]:lg:mb-0">
					<PageTitle title="Draft Build Spectrum" />
				</div>
				<Link
					to="/best-ball-zone"
					className="shrink-0 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
				>
					← Best Ball Zone
				</Link>
			</div>
			<p className="mt-2 text-sm text-gray-600 dark:text-gray-300 max-w-3xl lg:max-w-none text-pretty">
				Slide across the construction spectrum to see when to take each position. Use{' '}
				<strong className="font-semibold">Zero</strong> for stacked RB/WR runs at the poles, or{' '}
				<strong className="font-semibold">Hero</strong> for a single R1 anchor and a revisit window after a scalable gap.
			</p>

			{/* Controls */}
			<div className="mt-8 p-5 md:p-6 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-sm">
				<div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
					<div className="flex-1 max-w-2xl">
						<p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
							Construction spectrum
						</p>
						<div className="mt-2 flex flex-col gap-10">
							<ConstructionModeToggle
								mode={constructionMode}
								onChange={(mode) => setConstructionMode(normalizeConstructionMode(mode))}
							/>
							<SpectrumSlider
								value={spectrumValue}
								onChange={setSpectrumValue}
								constructionMode={constructionMode}
							/>
						</div>
					</div>

					<div className="flex flex-col sm:flex-row sm:items-start gap-6 sm:gap-8">
						<div>
							<label
								htmlFor="draft-scoring"
								className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3"
							>
								Scoring
							</label>
							<select
								id="draft-scoring"
								value={draftScoring}
								onChange={(e) => setDraftScoring(e.target.value)}
								className="block w-full min-w-[9.5rem] border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card rounded-lg px-3 py-1.5 text-sm font-medium text-gray-800 dark:text-dark-text"
							>
								{SCORING_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>

						<div className="sm:pl-4 sm:ml-2 sm:border-l sm:border-gray-200 dark:sm:border-dark-border">
							<label
								htmlFor="draft-format"
								className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3"
							>
								Format
							</label>
							<select
								id="draft-format"
								value={draftFormat}
								onChange={(e) => setDraftFormat(e.target.value)}
								className="block w-full min-w-[8.5rem] border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card rounded-lg px-3 py-1.5 text-sm font-medium text-gray-800 dark:text-dark-text"
							>
								{FORMAT_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>

						<div className="sm:pl-4 sm:ml-2 sm:border-l sm:border-gray-200 dark:sm:border-dark-border">
							<p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
								Draft rounds
							</p>
							<div className="flex flex-wrap gap-2.5 sm:gap-3">
								{ROUND_COUNT_OPTIONS.map((n) => (
									<button
										key={n}
										type="button"
										onClick={() => {
											setTotalRounds(n);
											setSelectedRound((r) => Math.min(r, n));
										}}
										className={`min-w-[2.75rem] px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${
											totalRounds === n
												? 'bg-blue-600 text-white border-blue-600'
												: 'bg-gray-100 border-gray-200 text-gray-800 dark:bg-gray-800 dark:border-dark-border dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 dark:hover:border-gray-500'
										}`}
									>
										{n}
									</button>
								))}
							</div>
						</div>
					</div>
				</div>

				<div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
					<EliteToggle
						label="Elite QB"
						description="Slot a top-4 QB in rounds 3–5; backup QBs late. Next 2–3 rounds refocus RB/WR."
						enabled={eliteQB}
						onChange={(checked) => {
							setEliteQB(checked);
							if (checked) setLateQB(false);
						}}
						accentClass="bg-blue-50 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
					/>
					<EliteToggle
						label="Late QB"
						description="No early QB — three QBs in rounds 12–18 (18-rd), spaced evenly with an early-half lean."
						enabled={lateQB}
						onChange={(checked) => {
							setLateQB(checked);
							if (checked) setEliteQB(false);
						}}
						accentClass="bg-blue-50 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
					/>
					<EliteToggle
						label="Elite TE"
						description="Slot a top-3 TE in rounds 2–4; backup TEs late. Next 2–3 rounds refocus RB/WR."
						enabled={eliteTE}
						onChange={(checked) => {
							setEliteTE(checked);
							if (checked) setLateTE(false);
						}}
						accentClass="bg-purple-50 dark:bg-purple-900 text-purple-800 dark:text-purple-200"
					/>
					<EliteToggle
						label="Late TE"
						description="No early TE — three TEs in rounds 12–18 (18-rd), spaced evenly with a late-half lean."
						enabled={lateTE}
						onChange={(checked) => {
							setLateTE(checked);
							if (checked) setEliteTE(false);
						}}
						accentClass="bg-purple-50 dark:bg-purple-900 text-purple-800 dark:text-purple-200"
					/>
				</div>

				{plan.constructionMode === 'hero' && plan.heroPauseUntil && (
					<p className="mt-3 text-xs text-gray-600 dark:text-gray-300">
						Hero gap: avoid the anchor position until round {plan.heroPauseUntil}
						{plan.heroRevisitRounds?.length > 0 &&
							` · Revisit R${plan.heroRevisitRounds.join(', R')}`}
					</p>
				)}

				{lateQB && plan.lateQBRounds?.length > 0 && (
					<p className="mt-3 text-xs text-gray-600 dark:text-gray-300">
						Late QB schedule: R{plan.lateQBRounds.join(', R')}
					</p>
				)}

				{lateTE && plan.lateTERounds?.length > 0 && (
					<p className="mt-3 text-xs text-gray-600 dark:text-gray-300">
						Late TE schedule: R{plan.lateTERounds.join(', R')}
					</p>
				)}

				{(eliteQB || eliteTE) && plan.eliteAnchors?.length > 0 && (
					<p className="mt-3 text-xs text-gray-600 dark:text-gray-300">
						Elite anchors:{' '}
						{plan.eliteAnchors.map((a) => `R${a.round} ${a.pos}`).join(' · ')}
						{plan.revisitRoundList?.length > 0 &&
							` · Revisit R${plan.revisitRoundList.join(', R')}`}
					</p>
				)}

				{/* Build identity */}
				<div className="mt-6 pt-6 border-t border-gray-100 dark:border-dark-border">
					<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
						<h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
							{plan.meta.label}
						</h2>
						<span className="text-sm font-medium text-blue-600 dark:text-blue-400">
							{plan.meta.spectrumLabel}
						</span>
						<span className="text-sm text-gray-500 dark:text-gray-400">
							· {plan.meta.shortLabel}
						</span>
					</div>
					<p className="mt-3 text-sm leading-relaxed text-gray-700 dark:text-gray-200">
						{plan.meta.philosophy}
					</p>
				</div>
			</div>

			<div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
				{/* Left: roster projection + rules */}
				<div className="space-y-6">
					<div className="p-5 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card">
						<h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
							Projected roster ({totalRounds} picks)
						</h3>
						<p className="mt-3 text-2xl font-mono font-bold tracking-tight text-gray-900 dark:text-dark-text">
							{plan.compositionLine}
						</p>
						<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
							QB · RB · WR · TE (max 3 QB, max 3 TE)
						</p>
						<div className="mt-4">
							<RosterCountPills counts={plan.rosterProjection} />
						</div>
						<p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
							{formatRosterCounts(plan.rosterProjection)} — target composition for this
							spectrum. Toggle Round map, Build curve, Focus map, or Roster shape for
							quantitative views.
						</p>

						<div className="mt-5 space-y-3">
							{plan.checkpoints.map((cp) => (
								<div
									key={cp.round}
									className="flex items-center justify-between text-sm border-b border-gray-100 dark:border-dark-border pb-2 last:border-0"
								>
									<span className="text-gray-600 dark:text-gray-300">{cp.label}</span>
									<span className="font-mono text-xs text-gray-800 dark:text-gray-200">
										{formatRosterCounts(cp.counts)}
									</span>
								</div>
							))}
						</div>
					</div>

					<div className="p-5 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card">
						<h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
							Execution rules
						</h3>
						<ul className="mt-3 space-y-2.5">
							{plan.meta.rules.map((rule, index) => (
								<li
									key={`${index}-${rule.slice(0, 32)}`}
									className="text-sm text-gray-700 dark:text-gray-200 flex gap-2 leading-snug"
								>
									<span className="text-blue-500 shrink-0">→</span>
									<span>{rule}</span>
								</li>
							))}
						</ul>
					</div>

					<div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-dark-border">
						<p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
							<strong className="text-gray-800 dark:text-gray-100">How to read this:</strong>{' '}
							The target line (e.g. 2-6-8-2) is your roster shape — QB and TE never exceed 3.
							Round cards show when to draft each position; charts show cumulative geometry,
							per-round focus weights, and composition shape vs. balanced. Pivot notes apply
							when you are ahead or behind pace vs. the target counts.
						</p>
					</div>
				</div>

				{/* Center + right: round grid + detail */}
				<div className="xl:col-span-2 space-y-6">
					<DraftSpectrumVisualPanel
						plan={plan}
						view={spectrumView}
						selectedRound={selectedRound}
						onViewChange={setSpectrumView}
						onRoundSelect={setSelectedRound}
					>
						<div className="flex justify-end mb-3">
							<div className="flex gap-3 text-xs text-gray-600 dark:text-gray-300">
								{POSITIONS.map((pos) => (
									<span key={pos} className="flex items-center gap-1">
										<span className={`w-2 h-2 rounded-sm ${POSITION_COLORS[pos].bg}`} />
										{pos}
									</span>
								))}
							</div>
						</div>

						<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
							{plan.rounds.map((round) => (
								<RoundCard
									key={round.round}
									round={round}
									isSelected={selectedRound === round.round}
									onSelect={setSelectedRound}
								/>
							))}
						</div>
					</DraftSpectrumVisualPanel>

					{activeRound && (
						<div
							className={`p-5 md:p-6 rounded-xl border-2 ${POSITION_COLORS[activeRound.primary].border} ${POSITION_COLORS[activeRound.primary].soft}`}
						>
							<div className="flex flex-wrap items-center gap-3 mb-4">
								<span className="text-3xl font-bold text-gray-900 dark:text-dark-text">
									Round {activeRound.round}
								</span>
								<PositionBadge position={activeRound.primary} size="lg" />
								{(activeRound.isHeroRbAnchor || activeRound.isHeroRbOpen) &&
									!activeRound.revisitPositionLabel && (
									<span className="text-xs font-bold uppercase tracking-wide text-green-700 dark:text-green-300 px-2 py-1 rounded bg-green-100 dark:bg-green-900">
										Hero RB
									</span>
								)}
								{(activeRound.isHeroWrAnchor || activeRound.isHeroWrOpen) &&
									!activeRound.revisitPositionLabel && (
									<span className="text-xs font-bold uppercase tracking-wide text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded bg-yellow-100 dark:bg-yellow-900">
										Hero WR
									</span>
								)}
								{activeRound.revisitPositionLabel && (
									<span className="text-xs font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200 px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 border border-dashed border-gray-400 dark:border-gray-500">
										{activeRound.revisitPositionLabel}
									</span>
								)}
								{activeRound.isLateQB && (
									<span className="text-xs font-bold uppercase tracking-wide text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded bg-yellow-100 dark:bg-yellow-900">
										Late QB
									</span>
								)}
								{activeRound.isLateTE && (
									<span className="text-xs font-bold uppercase tracking-wide text-purple-700 dark:text-purple-300 px-2 py-1 rounded bg-purple-100 dark:bg-purple-900">
										Late TE
									</span>
								)}
								{activeRound.isElitePick && (
									<span className="text-xs font-bold uppercase tracking-wide text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded bg-yellow-100 dark:bg-yellow-900">
										Elite {activeRound.eliteType}
									</span>
								)}
								<span className="text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-300 px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-dark-border">
									{activeRound.phaseLabel}
								</span>
							</div>

							<PositionBar weights={activeRound.weights} />

							<div className="mt-4 grid grid-cols-4 gap-2">
								{POSITIONS.map((pos) => (
									<div key={pos} className="text-center">
										<div className={`text-lg font-bold ${POSITION_COLORS[pos].text}`}>
											{Math.round(activeRound.weights[pos] * 100)}%
										</div>
										<div className="text-xs text-gray-500 dark:text-gray-400">{pos}</div>
									</div>
								))}
							</div>

							<div className="mt-5 space-y-3">
								<div>
									<p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
										Target tier
									</p>
									<p className="text-sm font-medium text-gray-900 dark:text-dark-text mt-0.5">
										{activeRound.tier}
									</p>
								</div>
								<div>
									<p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
										Draft action
									</p>
									<p className="text-sm text-gray-800 dark:text-gray-100 mt-0.5 leading-relaxed">
										{activeRound.action}
									</p>
								</div>
								{activeRound.pivot && (
									<div>
										<p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
											Pivot trigger
										</p>
										<p className="text-sm text-gray-700 dark:text-gray-200 mt-0.5">
											{activeRound.pivot}
										</p>
									</div>
								)}
								<div>
									<p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
										Roster after this pick
									</p>
									<p className="text-sm font-mono text-gray-800 dark:text-gray-100 mt-0.5">
										{formatRosterCounts(activeRound.cumulative)}
									</p>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
